import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/hooks/useAuth';
import { getCommerceId } from '@/lib/ownership';
import { uploadMediaFile, formatFileSize, MEDIA_LIMITS, uploadDigitalFile, DIGITAL_FILE_LIMIT_LABEL } from '@/hooks/useUpload';
import { normalizeCurrency } from '@/lib/currency';
import { compressImageFile } from '@/lib/imageCompress';
import { uploadToSeaweedFS } from '@/lib/seaweedfs';
import { getStoreCapabilities } from '@/lib/accountType';
import { MENU_ROLES } from '@/lib/menuRole';
import { PRODUCT_VISIBILITIES } from '@/lib/coreCatalog';
import { getOrganizationByCommerce } from '@/lib/zifekCore';
import { emitCoreActivity } from '@/lib/coreEventHooks';
import { createCoreMedia, setEntityTags } from '@/lib/coreSocle';
import { listCoreLanguages } from '@/lib/coreSystem';
import TagSelector from '@/components/feature/TagSelector';
import MediaLibraryPicker from '@/components/feature/MediaLibraryPicker';
import VoiceInputButton from '@/components/feature/VoiceInputButton';
import { insertTranscriptAtCursor } from '@/lib/voiceInsert';
import TranslationEditor, { type EntityTranslation, type TranslationLanguage } from '@/components/feature/TranslationEditor';

interface ProductCategory {
  id: number;
  name: string;
}

interface VariantDef {
  id: string;
  name: string;
  optionsText: string;
}

interface SkuRow {
  label: string;
  options: string[];
  price: string;
  stock: string;
  discount_enabled: boolean;
  discount_price: string;
}

interface MediaFile {
  file: File;
  preview: string;
}

type PricingMode = 'same' | 'per_variant';

export default function NewProductPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation();
  // Devise unique du marchand : choisie une seule fois dans Paramètres → Profil.
  const currency = normalizeCurrency(user?.monaie || 'MAD');
  // Le sélecteur « Rôle au menu » n'apparaît que pour les comptes Restauration.
  const caps = getStoreCapabilities(user?.type, user?.typecompte);
  const [categories, setCategories] = useState<ProductCategory[]>([]);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [categoryId, setCategoryId] = useState('');
  const [status, setStatus] = useState('draft');
  const [price, setPrice] = useState('');
  const [stock, setStock] = useState('0');
  const [costPrice, setCostPrice] = useState('');
  const [discountEnabled, setDiscountEnabled] = useState(false);
  const [discountPrice, setDiscountPrice] = useState('');
  const [pricingMode, setPricingMode] = useState<PricingMode>('same');
  const [hasVariants, setHasVariants] = useState(false);
  const [variantDefs, setVariantDefs] = useState<VariantDef[]>([]);
  const [skus, setSkus] = useState<SkuRow[]>([]);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [msg, setMsg] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const [coverFile, setCoverFile] = useState<MediaFile | null>(null);
  const [additionalFiles, setAdditionalFiles] = useState<MediaFile[]>([]);
  const [videoFile, setVideoFile] = useState<MediaFile | null>(null);
  const [mediaError, setMediaError] = useState<string | null>(null);
  const [seoTitle, setSeoTitle] = useState('');
  const [seoDescription, setSeoDescription] = useState('');
  const [seoKeywords, setSeoKeywords] = useState('');
  const [aiLoading, setAiLoading] = useState(false);
  const [aiMsg, setAiMsg] = useState<{ type: 'success' | 'error'; text: string; action?: 'pickImage' } | null>(null);
  // Panneau « Options avancées » (Entité centrale) replié par défaut pour
  // garder une page simple pour les commerces.
  const [advancedOpen, setAdvancedOpen] = useState(false);

  const [productType, setProductType] = useState<'physical' | 'digital'>('physical');
  const [menuRole, setMenuRole] = useState<string>('');
  const [digitalFile, setDigitalFile] = useState<MediaFile | null>(null);
  const [digitalNote, setDigitalNote] = useState('');

  // Entité centrale Produit (champs transverses multi-modules)
  const [sku, setSku] = useState('');
  const [barcode, setBarcode] = useState('');
  const [subcategory, setSubcategory] = useState('');
  const [visibility, setVisibility] = useState('public');
  const [stockTracking, setStockTracking] = useState(true);
  const [taxRate, setTaxRate] = useState('');
  const [taxIncluded, setTaxIncluded] = useState(false);

  // Socle central : tags (core_tags) + médiathèque (images)
  const [orgId, setOrgId] = useState<number | null>(null);
  const [tagIds, setTagIds] = useState<number[]>([]);
  const [libraryOpen, setLibraryOpen] = useState(false);
  const [libraryUrls, setLibraryUrls] = useState<string[]>([]);
  // Socle Langues : versions traduites (intitulé/description par langue)
  const [translationLangs, setTranslationLangs] = useState<TranslationLanguage[]>([]);
  const [translations, setTranslations] = useState<EntityTranslation>({});

  const coverInputRef = useRef<HTMLInputElement>(null);
  const descriptionRef = useRef<HTMLTextAreaElement>(null);
  const imagesInputRef = useRef<HTMLInputElement>(null);
  const videoInputRef = useRef<HTMLInputElement>(null);
  const digitalInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!user) return;
    // Isolation par commerce : uniquement les catégories de la boutique du marchand.
    supabase
      .from('categorie')
      .select('id, nom')
      .eq('idcommerce', getCommerceId(user))
      .order('sort_order', { ascending: true })
      .order('nom', { ascending: true })
      .then(({ data }) => {
        setCategories((data || []).map((c: { id: number; nom: string }) => ({ id: c.id, name: c.nom })));
      });
  }, [user]);

  useEffect(() => {
    if (!user) return;
    const cid = getCommerceId(user);
    if (!cid) return;
    getOrganizationByCommerce(cid)
      .then((org) => setOrgId(org?.id ?? null))
      .catch(() => setOrgId(null));
  }, [user]);

  useEffect(() => {
    // Langues actives (hors langue principale, déjà couverte par les champs du haut).
    listCoreLanguages()
      .then((langs) => {
        setTranslationLangs(
          langs
            .filter((l) => l.active && !l.is_default)
            .map((l) => ({ code: l.code, label: l.label, flag: l.flag, isDefault: false })),
        );
      })
      .catch(() => setTranslationLangs([]));
  }, []);

  const createPreview = useCallback((file: File): string => {
    return URL.createObjectURL(file);
  }, []);

  const revokePreview = useCallback((preview: string) => {
    URL.revokeObjectURL(preview);
  }, []);

  useEffect(() => {
    return () => {
      if (coverFile) revokePreview(coverFile.preview);
      additionalFiles.forEach((f) => revokePreview(f.preview));
      if (videoFile) revokePreview(videoFile.preview);
    };
  }, []);

  const validateFile = (file: File, isVideo: boolean): string | null => {
    const limit = isVideo ? MEDIA_LIMITS.video : MEDIA_LIMITS.image;
    if (file.size > limit.maxBytes) {
      return `Le fichier "${file.name}" dépasse la limite de ${limit.label} (${formatFileSize(file.size)})`;
    }
    if (isVideo) {
      if (!file.type.startsWith('video/')) return `"${file.name}" n'est pas une vidéo valide`;
    } else {
      if (!file.type.startsWith('image/')) return `"${file.name}" n'est pas une image valide`;
    }
    return null;
  };

  const handleCoverChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setMediaError(null);
    const f = e.target.files?.[0];
    if (!f) return;
    const err = validateFile(f, false);
    if (err) { setMediaError(err); return; }
    if (coverFile) revokePreview(coverFile.preview);
    setCoverFile({ file: f, preview: createPreview(f) });
  };

  const handleAdditionalImages = (e: React.ChangeEvent<HTMLInputElement>) => {
    setMediaError(null);
    const files = Array.from(e.target.files || []);
    for (const f of files) {
      const err = validateFile(f, false);
      if (err) { setMediaError(err); return; }
    }
    const newFiles = files.map((f) => ({ file: f, preview: createPreview(f) }));
    setAdditionalFiles((prev) => [...prev, ...newFiles]);
    if (imagesInputRef.current) imagesInputRef.current.value = '';
  };

  const handleVideoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setMediaError(null);
    const f = e.target.files?.[0];
    if (!f) return;
    const err = validateFile(f, true);
    if (err) { setMediaError(err); return; }
    if (videoFile) revokePreview(videoFile.preview);
    setVideoFile({ file: f, preview: createPreview(f) });
  };

  const removeCover = () => {
    if (coverFile) revokePreview(coverFile.preview);
    setCoverFile(null);
    if (coverInputRef.current) coverInputRef.current.value = '';
  };

  const removeAdditionalImage = (idx: number) => {
    setAdditionalFiles((prev) => {
      const next = [...prev];
      revokePreview(next[idx].preview);
      next.splice(idx, 1);
      return next;
    });
  };

  const removeVideo = () => {
    if (videoFile) revokePreview(videoFile.preview);
    setVideoFile(null);
    if (videoInputRef.current) videoInputRef.current.value = '';
  };

  const handleDigitalChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (digitalFile) revokePreview(digitalFile.preview);
    setDigitalFile({ file: f, preview: createPreview(f) });
    if (digitalInputRef.current) digitalInputRef.current.value = '';
  };

  const removeDigital = () => {
    if (digitalFile) revokePreview(digitalFile.preview);
    setDigitalFile(null);
    if (digitalInputRef.current) digitalInputRef.current.value = '';
  };

  const generateCombinations = (defs: VariantDef[]): string[][] => {
    if (defs.length === 0 || defs.some((d) => !d.optionsText.trim())) return [];
    const optionArrays = defs.map((d) =>
      d.optionsText.split(',').map((o) => o.trim()).filter(Boolean)
    );
    if (optionArrays.some((arr) => arr.length === 0)) return [];
    let combos: string[][] = [[]];
    for (const arr of optionArrays) {
      const next: string[][] = [];
      for (const combo of combos) {
        for (const opt of arr) {
          next.push([...combo, opt]);
        }
      }
      combos = next;
    }
    return combos;
  };

  const rebuildSkus = (defs: VariantDef[]) => {
    const combos = generateCombinations(defs);
    setSkus(
      combos.map((combo) => ({
        label: combo.join(' / '),
        options: combo,
        price: pricingMode === 'same' ? '' : price,
        stock: pricingMode === 'same' ? '' : stock,
        discount_enabled: false,
        discount_price: '',
      }))
    );
  };

  const addVariantDef = () => {
    const newDef: VariantDef = { id: crypto.randomUUID(), name: '', optionsText: '' };
    const updated = [...variantDefs, newDef];
    setVariantDefs(updated);
  };

  const removeVariantDef = (id: string) => {
    const updated = variantDefs.filter((d) => d.id !== id);
    setVariantDefs(updated);
    rebuildSkus(updated);
  };

  const updateVariantDef = (id: string, field: 'name' | 'optionsText', value: string) => {
    const updated = variantDefs.map((d) => (d.id === id ? { ...d, [field]: value } : d));
    setVariantDefs(updated);
    if (field === 'optionsText') rebuildSkus(updated);
  };

  const updateSkuField = (index: number, field: keyof SkuRow, value: string | boolean) => {
    setSkus((prev) => prev.map((s, i) => (i === index ? { ...s, [field]: value } : s)));
  };

  const fileToDataUrl = (file: File): Promise<string> => new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result));
    reader.onerror = () => reject(new Error('Lecture de l’image impossible'));
    reader.readAsDataURL(file);
  });

  // Prépare une image pour l'IA. On envoie EN PRIORITÉ son contenu (base64)
  // compressé — les octets voyagent directement, donc le modèle voit à coup sûr
  // l'image (plus de « aucune image transmise »). Le lien hébergé temporaire est
  // fourni en secours si l'hébergement réussit.
  const prepareImageForAi = async (file: File): Promise<{ inline: string; url: string }> => {
    const compressed = await compressImageFile(file, { maxDimension: 640, maxBytes: 90 * 1024 });
    let inline = '';
    try { inline = await fileToDataUrl(compressed); } catch { /* le lien prendra le relais */ }
    let url = '';
    try {
      const buffer = await compressed.arrayBuffer();
      const uid = user?.id ?? 'anon';
      url = await uploadToSeaweedFS(
        new Uint8Array(buffer),
        `produit-ia-${Date.now()}.jpg`,
        'image/jpeg',
        `users/${uid}/ia-temp`,
      );
    } catch { /* le base64 suffit */ }
    return { inline, url };
  };

  // ── Remplissage par IA : analyse les images et remplit nom, description + SEO ──
  const handleAiFill = async () => {
    const files: File[] = [];
    if (coverFile) files.push(coverFile.file);
    additionalFiles.forEach((f) => files.push(f.file));
    const hasLibraryImage = libraryUrls.length > 0;

    // Une image est OBLIGATOIRE : sans image, aucune analyse n'est possible.
    if (files.length === 0 && !hasLibraryImage) {
      setAiMsg({ type: 'error', text: 'Veuillez choisir une image du produit avant de lancer le remplissage par IA (section Médias).', action: 'pickImage' });
      return;
    }

    setAiLoading(true);
    setAiMsg(null);
    try {
      // 1) Préparation des images : contenu base64 (garanti) + lien de secours.
      let images: string[] = [];
      let inlineImages: string[] = [];
      if (files.length > 0) {
        const prepared = await Promise.all(files.slice(0, 2).map(prepareImageForAi));
        inlineImages = prepared.map((p) => p.inline).filter(Boolean);
        images = prepared.map((p) => p.url).filter(Boolean);
      } else {
        images = libraryUrls.slice(0, 2);
      }
      const { data, error } = await supabase.functions.invoke('product-ai-fill', {
        body: {
          images,
          inlineImages,
          name: name.trim(),
          category: categories.find((c) => String(c.id) === categoryId)?.name || '',
          currency,
        },
      });
      if (error) {
        // Supabase masque le corps de la réponse derrière un message générique
        // « Edge Function returned a non-2xx status code ». On récupère le vrai
        // message renvoyé par la fonction pour informer clairement le marchand.
        let detail = error.message || 'Erreur lors du remplissage IA';
        try {
          const ctx = (error as { context?: Response }).context;
          if (ctx && typeof ctx.json === 'function') {
            const body = (await ctx.json()) as { error?: string; message?: string };
            detail = body?.error || body?.message || detail;
          }
        } catch { /* on garde le message par défaut */ }
        throw new Error(detail);
      }
      const payload = data as { error?: string; result?: Record<string, string> } | null;
      if (payload?.error) throw new Error(payload.error);
      const r = payload?.result || {};
      let applied = 0;
      if (r.name && !name.trim()) { setName(r.name); applied += 1; }
      if (r.description) { setDescription(r.description); applied += 1; }
      if (r.price && !price.trim()) { setPrice(r.price); applied += 1; }
      if (r.cost_price && !costPrice.trim()) { setCostPrice(r.cost_price); applied += 1; }
      if (r.stock && (!stock.trim() || stock === '0')) { setStock(r.stock); applied += 1; }
      if (r.category && !categoryId) {
        const wanted = String(r.category).toLowerCase();
        const match = categories.find(
          (c) => c.name.toLowerCase().includes(wanted) || wanted.includes(c.name.toLowerCase()),
        );
        if (match) { setCategoryId(String(match.id)); applied += 1; }
      }
      if (r.seo_title) { setSeoTitle(r.seo_title); applied += 1; }
      if (r.seo_description) { setSeoDescription(r.seo_description); applied += 1; }
      const keywords = r.seo_keywords || r.tags || '';
      if (keywords) { setSeoKeywords(keywords); applied += 1; }
      // Aucun champ rempli = l'IA n'a rien produit d'exploitable → on le dit clairement
      // au lieu d'afficher un faux message de réussite.
      if (applied === 0) {
        throw new Error("L'IA n'a renvoyé aucun contenu exploitable. Réessayez avec une image plus nette.");
      }
      setAiMsg({ type: 'success', text: 'Fiche produit complétée par IA ✨ (nom, description, catégorie, prix, stock et SEO). Vérifiez et ajustez si besoin avant d’enregistrer.' });
    } catch (err: unknown) {
      setAiMsg({ type: 'error', text: err instanceof Error ? err.message : 'Erreur lors du remplissage IA' });
    } finally {
      setAiLoading(false);
    }
  };

  const validate = (): boolean => {
    const errs: Record<string, string> = {};
    if (!name.trim()) errs.name = t('prf.name_required', { defaultValue: 'Le nom est requis' });
    if (!price.trim() || isNaN(Number(price)) || Number(price) < 0) errs.price = t('prf.invalid_price', { defaultValue: 'Prix invalide' });
    if (stock.trim() && (isNaN(Number(stock)) || Number(stock) < 0)) errs.stock = t('prf.invalid_stock', { defaultValue: 'Stock invalide' });
    if (productType === 'digital' && !digitalFile) errs.digitalFile = 'Veuillez téléverser le fichier à livrer.';
    if (discountEnabled && discountPrice.trim() && (isNaN(Number(discountPrice)) || Number(discountPrice) < 0)) {
      errs.discountPrice = t('prf.invalid_promo', { defaultValue: 'Prix promo invalide' });
    }
    if (hasVariants && pricingMode !== 'same') {
      for (let i = 0; i < skus.length; i++) {
        const s = skus[i];
        if (!s.price.trim() || isNaN(Number(s.price)) || Number(s.price) < 0) {
          errs[`sku_price_${i}`] = `Prix invalide pour "${s.label}"`;
        }
      }
    }
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;
    if (mediaError) { setMsg({ type: 'error', text: mediaError }); return; }

    setSaving(true);
    setUploading(true);
    setMsg(null);

    try {
      const media: { url: string; type: string }[] = [];
      const folder = `products/${Date.now()}`;
      const ownerId = getCommerceId(user);
      const ownerName = user?.name || user?.user_name || 'Marchand';

      // Enregistre le média uploadé dans le socle Média central (best-effort).
      const registerMedia = async (url: string, title: string, mediaType: string) => {
        try {
          await createCoreMedia({
            ownerId,
            ownerName,
            title,
            filepath: url,
            mediaType,
            organizationId: orgId,
            tenantId: null,
            visibility: 'organization',
          });
        } catch { /* best-effort */ }
      };

      if (coverFile) {
        const url = await uploadMediaFile(coverFile.file, `${folder}/cover`);
        media.push({ url, type: 'image' });
        await registerMedia(url, name.trim() || `Produit ${Date.now()}`, 'image');
      }

      for (const f of additionalFiles) {
        const url = await uploadMediaFile(f.file, `${folder}/images`);
        media.push({ url, type: 'image' });
        await registerMedia(url, f.file.name || 'Image produit', 'image');
      }

      if (videoFile) {
        const url = await uploadMediaFile(videoFile.file, `${folder}/video`);
        media.push({ url, type: 'video' });
        await registerMedia(url, videoFile.file.name || 'Vidéo produit', 'video');
      }

      // Images réutilisées depuis la médiathèque
      libraryUrls.forEach((url) => media.push({ url, type: 'image' }));

      let digitalFileUrl: string | null = null;
      if (productType === 'digital' && digitalFile) {
        digitalFileUrl = await uploadDigitalFile(digitalFile.file);
      }

      setUploading(false);

      const productPayload: Record<string, unknown> = {
        name: name.trim(),
        description: description.trim() || null,
        seo_title: seoTitle.trim() || null,
        seo_description: seoDescription.trim() || null,
        seo_keywords: seoKeywords.trim() || null,
        category_id: categoryId ? parseInt(categoryId, 10) : null,
        status,
        price: parseFloat(price) || 0,
        cost_price: costPrice.trim() ? parseFloat(costPrice) : null,
        currency,
        stock: productType === 'digital' ? 0 : (parseInt(stock, 10) || 0),
        product_type: productType,
        digital_file_url: productType === 'digital' ? digitalFileUrl : null,
        digital_file_name: productType === 'digital' && digitalFile ? digitalFile.file.name : null,
        digital_delivery_note: productType === 'digital' ? (digitalNote.trim() || null) : null,
        discount_enabled: discountEnabled,
        discount_price: discountEnabled && discountPrice.trim() ? parseFloat(discountPrice) : null,
        pricing_mode: hasVariants ? (pricingMode === 'per_variant' ? 1 : 0) : 0,
        media: JSON.stringify(media),
        menu_role: menuRole || null,
        sku: sku.trim() || null,
        barcode: barcode.trim() || null,
        subcategory: subcategory.trim() || null,
        translation: translations,
        visibility,
        stock_tracking_enabled: productType === 'physical' ? stockTracking : false,
        tax_config: { rate: parseFloat(taxRate) || 0, included: taxIncluded },
        owner: user?.id,
        idcommerce: getCommerceId(user),
        ville: user?.Ville || null,
        pays: user?.Pays || null,
      };

      const { data: productData, error: insertError } = await supabase
        .from('product_items')
        .insert(productPayload)
        .select('id')
        .single();

      if (insertError) throw insertError;
      const productId = productData.id;

      if (hasVariants && variantDefs.length > 0) {
        for (let i = 0; i < variantDefs.length; i++) {
          const def = variantDefs[i];
          const opts = def.optionsText.split(',').map((o) => o.trim()).filter(Boolean);
          await supabase.from('product_variants').insert({
            product_id: productId,
            name: def.name.trim(),
            options: JSON.stringify(opts),
            sort_order: i,
          });
        }

        const skuPayloads = skus.map((s) => ({
          product_id: productId,
          label: s.label,
          options: JSON.stringify(s.options),
          price: pricingMode === 'per_variant' ? parseFloat(s.price) || 0 : null,
          stock: parseInt(s.stock, 10) || 0,
          discount_enabled: s.discount_enabled,
          discount_price: s.discount_enabled && s.discount_price.trim() ? parseFloat(s.discount_price) : null,
        }));
        await supabase.from('product_skus').insert(skuPayloads);
      }

      if (orgId) {
        try { await setEntityTags(orgId, 'product', productId, tagIds); } catch { /* best-effort */ }
      }

      // Événement du socle : produit créé → re-index auto + consommateurs.
      await emitCoreActivity({
        commerceId: getCommerceId(user),
        eventKey: 'product.created',
        entityType: 'product',
        entityId: productId,
        title: `Produit créé — ${name.trim()}`,
        description: description.trim() || null,
        amount: parseFloat(price) || 0,
        currency,
        payload: { name: name.trim(), price: parseFloat(price) || 0, status },
        actorName: user?.name ?? null,
        actorUserId: user?.id ?? null,
        source: 'products',
      });

      setMsg({ type: 'success', text: t('prf.created', { defaultValue: 'Produit créé avec succès !' }) });
      setTimeout(() => navigate('/dashboard/products'), 1200);
    } catch (err: unknown) {
      const message =
        err instanceof Error
          ? err.message
          : (typeof err === 'object' && err !== null && 'message' in err)
            ? String((err as Record<string, unknown>).message)
            : 'Erreur lors de la création du produit';
      setMsg({ type: 'error', text: message });
    } finally {
      setSaving(false);
      setUploading(false);
    }
  };

  const dropZoneClasses = (hasFile: boolean) =>
    `relative border-2 border-dashed rounded-xl transition-all cursor-pointer flex flex-col items-center justify-center gap-2 ${hasFile ? 'border-primary-200/70 bg-primary-50/30 p-0 min-h-[180px]' : 'border-background-300/60 bg-background-50 hover:border-primary-300/50 hover:bg-background-100/50 p-6 min-h-[140px]'}`;

  return (
    <div className="p-4 md:p-6 max-w-4xl">
      <div className="flex items-center gap-3 mb-6">
        <button onClick={() => navigate('/dashboard/products')} className="w-9 h-9 flex items-center justify-center rounded-full bg-background-50 border border-background-200/70 text-foreground-500 hover:text-foreground-800 hover:bg-background-100 transition-colors cursor-pointer">
          <i className="ri-arrow-left-line"></i>
        </button>
        <div>
          <h2 className="text-xl md:text-2xl font-bold font-heading text-foreground-950">{t('prf.new_title', { defaultValue: 'Nouveau produit' })}</h2>
          <p className="text-sm text-foreground-500 mt-1">{t('prf.new_subtitle', { defaultValue: 'Ajoutez un produit à votre catalogue' })}</p>
        </div>
      </div>

      {msg && (
        <div className={`mb-5 px-4 py-3 rounded-lg text-sm font-medium ${msg.type === 'success' ? 'bg-accent-50 text-accent-700' : 'bg-red-50 text-red-600'}`}>
          {msg.text}
        </div>
      )}

      {uploading && (
        <div className="mb-5 px-4 py-3 rounded-lg text-sm font-medium bg-secondary-50 text-secondary-700 flex items-center gap-2">
          <i className="ri-loader-4-line animate-spin"></i> {t('prf.uploading_media', { defaultValue: 'Téléchargement des médias en cours...' })}
        </div>
      )}

      {/* Remplissage par IA — accès direct en haut de page */}
      <div className="mb-6 bg-primary-50 border border-primary-200/70 rounded-xl p-5">
        <div className="flex flex-col md:flex-row md:items-center gap-4">
          <div className="w-12 h-12 rounded-full bg-primary-500 text-background-50 flex items-center justify-center flex-shrink-0">
            <i className="ri-sparkling-2-line text-2xl"></i>
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="text-base font-bold font-heading text-foreground-900">Remplissage par IA</h3>
            <p className="text-xs text-foreground-600 mt-0.5">
              Téléversez d’abord les images du produit (section Médias ci-dessous), puis cliquez : l’IA analyse les images et remplit automatiquement le nom, la description, la catégorie, le prix conseillé, le stock, le coût d’achat et tous les champs SEO pour un meilleur référencement sur Google.
            </p>
            {aiMsg && (
              <div className={`mt-2.5 px-3 py-2 rounded-lg text-xs ${aiMsg.type === 'success' ? 'bg-accent-100 text-accent-900' : 'bg-red-50 text-red-600'}`}>
                <div className="flex flex-wrap items-center gap-2">
                  <span>{aiMsg.text}</span>
                  {aiMsg.action === 'pickImage' && (
                    <button
                      type="button"
                      onClick={() => coverInputRef.current?.click()}
                      className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-primary-500 text-background-50 font-semibold whitespace-nowrap hover:bg-primary-600 transition-colors cursor-pointer"
                    >
                      <i className="ri-image-add-line"></i> Choisir une image
                    </button>
                  )}
                </div>
              </div>
            )}
          </div>
          <button
            type="button"
            onClick={handleAiFill}
            disabled={aiLoading}
            className="inline-flex items-center justify-center gap-2 px-5 py-2.5 bg-primary-500 text-background-50 rounded-full text-sm font-semibold whitespace-nowrap hover:bg-primary-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors cursor-pointer flex-shrink-0"
          >
            {aiLoading ? (<><i className="ri-loader-4-line animate-spin"></i>Analyse en cours...</>) : (<><i className="ri-magic-line"></i>Remplir par IA</>)}
          </button>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Infos générales */}
        <div className="bg-background-50 border border-background-200/70 rounded-xl p-6 space-y-5">
          <h3 className="text-base font-bold font-heading text-foreground-900 pb-3 border-b border-background-200/70">{t('prf.general_info', { defaultValue: 'Informations générales' })}</h3>

          <div>
            <label className="block text-sm font-semibold text-foreground-800 mb-2">Type de produit</label>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <button
                type="button"
                onClick={() => setProductType('physical')}
                className={`text-left p-3 rounded-lg border transition-colors cursor-pointer ${productType === 'physical' ? 'border-primary-400 bg-primary-50/50' : 'border-background-200/70 hover:border-background-300/60'}`}
              >
                <div className="flex items-center gap-2 mb-1">
                  <i className={`ri-box-3-line text-base ${productType === 'physical' ? 'text-primary-600' : 'text-foreground-400'}`}></i>
                  <span className="text-sm font-semibold text-foreground-900">Produit physique</span>
                </div>
                <span className="text-xs text-foreground-500">Stock, livraison, expédition</span>
              </button>
              <button
                type="button"
                onClick={() => setProductType('digital')}
                className={`text-left p-3 rounded-lg border transition-colors cursor-pointer ${productType === 'digital' ? 'border-secondary-400 bg-secondary-50/50' : 'border-background-200/70 hover:border-background-300/60'}`}
              >
                <div className="flex items-center gap-2 mb-1">
                  <i className={`ri-download-cloud-2-line text-base ${productType === 'digital' ? 'text-secondary-600' : 'text-foreground-400'}`}></i>
                  <span className="text-sm font-semibold text-foreground-900">Produit digital</span>
                </div>
                <span className="text-xs text-foreground-500">Livre, vidéo, PDF… livré par fichier</span>
              </button>
            </div>
          </div>

          <div>
            <label className="block text-sm font-semibold text-foreground-800 mb-1.5">{t('prf.product_name', { defaultValue: 'Nom du produit' })} <span className="text-red-500">*</span></label>
            <input type="text" value={name} onChange={(e) => setName(e.target.value)} placeholder={t('prf.product_name_ph', { defaultValue: 'Ex: T-shirt Premium' })}
              className={`w-full px-3 py-2.5 bg-background-50 border rounded-lg text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300 transition-colors ${errors.name ? 'border-red-300' : 'border-background-200/70'}`} />
            {errors.name && <p className="text-xs text-red-500 mt-1">{errors.name}</p>}
          </div>

          <div>
            <div className="flex items-center justify-between gap-2 mb-1.5">
              <label className="block text-sm font-semibold text-foreground-800">{t('prf.description', { defaultValue: 'Description' })}</label>
              <VoiceInputButton
                userId={user?.id}
                title="Dicter la description"
                onTranscript={(text) => {
                  setDescription((prev) => insertTranscriptAtCursor(descriptionRef.current, prev, text));
                  descriptionRef.current?.focus();
                }}
              />
            </div>
            <textarea ref={descriptionRef} value={description} onChange={(e) => setDescription(e.target.value)} placeholder={t('prf.description_ph', { defaultValue: 'Décrivez votre produit...' })} rows={3}
              className="w-full px-3 py-2.5 bg-background-50 border border-background-200/70 rounded-lg text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300 transition-colors resize-none" />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <div>
              <label className="block text-sm font-semibold text-foreground-800 mb-1.5">{t('prf.category', { defaultValue: 'Catégorie' })}</label>
              <select value={categoryId} onChange={(e) => setCategoryId(e.target.value)}
                className="w-full px-3 py-2.5 bg-background-50 border border-background-200/70 rounded-lg text-sm text-foreground-900 focus:outline-none focus:border-primary-300 cursor-pointer">
                <option value="">{t('prf.no_category', { defaultValue: 'Aucune catégorie' })}</option>
                {categories.map((cat) => (<option key={cat.id} value={String(cat.id)}>{cat.name}</option>))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-semibold text-foreground-800 mb-1.5">{t('prf.status', { defaultValue: 'Statut' })}</label>
              <select value={status} onChange={(e) => setStatus(e.target.value)}
                className="w-full px-3 py-2.5 bg-background-50 border border-background-200/70 rounded-lg text-sm text-foreground-900 focus:outline-none focus:border-primary-300 cursor-pointer">
                <option value="draft">{t('prf.status_draft', { defaultValue: 'Brouillon' })}</option>
                <option value="active">{t('prf.status_active', { defaultValue: 'Actif' })}</option>
                <option value="inactive">{t('prf.status_inactive', { defaultValue: 'Inactif' })}</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-semibold text-foreground-800 mb-1.5">{t('prf.price', { defaultValue: 'Prix' })} ({currency}) <span className="text-red-500">*</span></label>
              <input type="number" step="0.01" min="0" value={price} onChange={(e) => setPrice(e.target.value)} placeholder="0.00"
                className={`w-full px-3 py-2.5 bg-background-50 border rounded-lg text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300 transition-colors ${errors.price ? 'border-red-300' : 'border-background-200/70'}`} />
              {errors.price && <p className="text-xs text-red-500 mt-1">{errors.price}</p>}
            </div>
            <div>
              <label className="block text-sm font-semibold text-foreground-800 mb-1.5">{t('prf.currency', { defaultValue: 'Devise' })}</label>
              <div className="flex items-center gap-2 w-full px-3 py-2.5 bg-background-100 border border-background-200/70 rounded-lg text-sm text-foreground-700" title="Devise unique de votre compte">
                <i className="ri-lock-line text-foreground-400"></i>
                <span className="font-semibold">{currency}</span>
              </div>
              <p className="text-[11px] text-foreground-400 mt-1">Définie dans Paramètres&nbsp;→&nbsp;Profil.</p>
            </div>
          </div>

          {caps.restaurant && (
          <div>
            <label className="block text-sm font-semibold text-foreground-800 mb-1.5">Rôle au menu</label>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2">
              <button
                type="button"
                onClick={() => setMenuRole('')}
                className={`flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg border text-xs font-medium whitespace-nowrap transition-colors cursor-pointer ${menuRole === '' ? 'border-primary-400 bg-primary-50/60 text-primary-700' : 'border-background-200/70 text-foreground-600 hover:bg-background-100'}`}
              >
                <i className="ri-forbid-2-line"></i>
                Aucun
              </button>
              {MENU_ROLES.map((r) => (
                <button
                  key={r.key}
                  type="button"
                  onClick={() => setMenuRole(r.key)}
                  className={`flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg border text-xs font-medium whitespace-nowrap transition-colors cursor-pointer ${menuRole === r.key ? 'border-primary-400 bg-primary-50/60 text-primary-700' : 'border-background-200/70 text-foreground-600 hover:bg-background-100'}`}
                >
                  <i className={r.icon}></i>
                  {r.label}
                </button>
              ))}
            </div>
            <p className="text-[11px] text-foreground-400 mt-1.5">Structure votre carte : les plats sont regroupés par section (entrées, plats, desserts, boissons) sur votre site public.</p>
          </div>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {productType === 'physical' && (
            <div>
              <label className="block text-sm font-semibold text-foreground-800 mb-1.5">{t('prf.stock', { defaultValue: 'Stock' })}</label>
              <input type="number" min="0" value={stock} onChange={(e) => setStock(e.target.value)} placeholder="0"
                className={`w-full px-3 py-2.5 bg-background-50 border rounded-lg text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300 transition-colors ${errors.stock ? 'border-red-300' : 'border-background-200/70'}`} />
              {errors.stock && <p className="text-xs text-red-500 mt-1">{errors.stock}</p>}
            </div>
            )}
            <div>
              <label className="block text-sm font-semibold text-foreground-800 mb-1.5">{t('prf.cost_price', { defaultValue: 'Coût d’achat' })} ({currency})</label>
              <input type="number" step="0.01" min="0" value={costPrice} onChange={(e) => setCostPrice(e.target.value)} placeholder="0.00"
                className="w-full px-3 py-2.5 bg-background-50 border border-background-200/70 rounded-lg text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300 transition-colors" />
              <p className="text-[11px] text-foreground-400 mt-1">Utilisé pour calculer votre marge et votre rentabilité.</p>
            </div>
          </div>

          <div className="bg-background-100/70 rounded-lg p-4">
            <label className="flex items-center gap-3 cursor-pointer">
              <input type="checkbox" checked={discountEnabled} onChange={(e) => setDiscountEnabled(e.target.checked)}
                className="w-4 h-4 rounded border-background-300/60 text-primary-500 focus:ring-primary-400 cursor-pointer" />
              <span className="text-sm font-semibold text-foreground-800">{t('prf.enable_promo', { defaultValue: 'Activer la promotion' })}</span>
            </label>
            {discountEnabled && (
              <div className="mt-3">
                <label className="block text-sm font-medium text-foreground-700 mb-1">{t('prf.promo_price', { defaultValue: 'Prix promotionnel' })} ({currency})</label>
                <input type="number" step="0.01" min="0" value={discountPrice} onChange={(e) => setDiscountPrice(e.target.value)} placeholder="0.00"
                  className={`w-full px-3 py-2.5 bg-background-50 border rounded-lg text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300 transition-colors ${errors.discountPrice ? 'border-red-300' : 'border-background-200/70'}`} />
                {errors.discountPrice && <p className="text-xs text-red-500 mt-1">{errors.discountPrice}</p>}
              </div>
            )}
          </div>
        </div>

        {/* Médias */}
        <div className="bg-background-50 border border-background-200/70 rounded-xl p-6 space-y-5">
          <div className="flex flex-wrap items-center justify-between gap-3 pb-3 border-b border-background-200/70">
            <h3 className="text-base font-bold font-heading text-foreground-900">{t('prf.media', { defaultValue: 'Médias' })}</h3>
            <button type="button" onClick={() => setLibraryOpen(true)} className="flex items-center gap-2 px-3.5 py-2 rounded-full bg-secondary-50 text-secondary-700 text-xs font-semibold whitespace-nowrap hover:bg-secondary-100 transition-colors cursor-pointer">
              <i className="ri-image-2-line"></i> Choisir depuis la médiathèque
            </button>
          </div>

          {mediaError && (
            <div className="px-3 py-2 bg-red-50 text-red-600 rounded-lg text-xs font-medium">{mediaError}</div>
          )}

          {/* Image de couverture */}
          <div>
            <label className="block text-sm font-semibold text-foreground-800 mb-2">{t('prf.cover_image', { defaultValue: 'Image de couverture' })}</label>
            <input ref={coverInputRef} type="file" accept={MEDIA_LIMITS.image.accept} onChange={handleCoverChange} className="hidden" />
            <div onClick={() => coverInputRef.current?.click()} className={dropZoneClasses(!!coverFile)}>
              {coverFile ? (
                <div className="relative w-full h-full rounded-xl overflow-hidden">
                  <img src={coverFile.preview} alt="Couverture" className="w-full h-full object-cover rounded-xl" />
                  <button type="button" onClick={(ev) => { ev.stopPropagation(); removeCover(); }}
                    className="absolute top-2 right-2 w-7 h-7 flex items-center justify-center rounded-full bg-black/50 text-white hover:bg-red-500 transition-colors cursor-pointer">
                    <i className="ri-close-line text-sm"></i>
                  </button>
                  <div className="absolute bottom-2 left-2 px-2 py-0.5 bg-black/50 text-white rounded text-xs">
                    {formatFileSize(coverFile.file.size)}
                  </div>
                </div>
              ) : (
                <>
                  <div className="w-10 h-10 flex items-center justify-center rounded-full bg-background-100 text-foreground-400">
                    <i className="ri-image-line text-xl"></i>
                  </div>
                  <div className="text-center">
                    <p className="text-sm text-foreground-600 font-medium">{t('prf.click_drag_image', { defaultValue: 'Cliquez ou glissez une image' })}</p>
                    <p className="text-xs text-foreground-400 mt-0.5">Max {MEDIA_LIMITS.image.label} — JPG, PNG, WebP, GIF</p>
                  </div>
                </>
              )}
            </div>
          </div>

          {/* Images supplémentaires */}
          <div>
            <label className="block text-sm font-semibold text-foreground-800 mb-2">{t('prf.additional_images', { defaultValue: 'Images supplémentaires' })} ({additionalFiles.length})</label>
            <div className="flex flex-wrap gap-3">
              {additionalFiles.map((f, idx) => (
                <div key={f.preview} className="relative w-24 h-24 rounded-lg border border-background-200/70 overflow-hidden bg-background-100 flex-shrink-0 group">
                  <img src={f.preview} alt={`Image ${idx + 1}`} className="w-full h-full object-cover" />
                  <button type="button" onClick={() => removeAdditionalImage(idx)}
                    className="absolute top-1 right-1 w-5 h-5 flex items-center justify-center rounded-full bg-black/50 text-white hover:bg-red-500 transition-colors cursor-pointer opacity-0 group-hover:opacity-100">
                    <i className="ri-close-line text-xs"></i>
                  </button>
                  <div className="absolute bottom-1 left-1 px-1.5 py-0.5 bg-black/50 text-white rounded text-[10px]">{formatFileSize(f.file.size)}</div>
                </div>
              ))}
              <input ref={imagesInputRef} type="file" accept={MEDIA_LIMITS.image.accept} multiple onChange={handleAdditionalImages} className="hidden" />
              <div onClick={() => imagesInputRef.current?.click()}
                className="w-24 h-24 rounded-lg border-2 border-dashed border-background-300/60 flex flex-col items-center justify-center gap-1 cursor-pointer hover:border-primary-300/50 hover:bg-background-100/50 transition-colors flex-shrink-0">
                <i className="ri-add-line text-foreground-400"></i>
                <span className="text-[10px] text-foreground-400">{t('prf.add', { defaultValue: 'Ajouter' })}</span>
              </div>
            </div>
            <p className="text-xs text-foreground-400 mt-1.5">Max {MEDIA_LIMITS.image.label} par image</p>
          </div>

          {/* Images de la médiathèque */}
          {libraryUrls.length > 0 && (
            <div>
              <label className="block text-sm font-semibold text-foreground-800 mb-2">Images de la médiathèque ({libraryUrls.length})</label>
              <div className="flex flex-wrap gap-3">
                {libraryUrls.map((url) => (
                  <div key={url} className="relative w-24 h-24 rounded-lg border border-background-200/70 overflow-hidden bg-background-100 flex-shrink-0 group">
                    <img src={url} alt="Média" className="w-full h-full object-cover" />
                    <button type="button" onClick={() => setLibraryUrls((prev) => prev.filter((u) => u !== url))}
                      className="absolute top-1 right-1 w-5 h-5 flex items-center justify-center rounded-full bg-black/50 text-white hover:bg-red-500 transition-colors cursor-pointer opacity-0 group-hover:opacity-100">
                      <i className="ri-close-line text-xs"></i>
                    </button>
                  </div>
                ))}
              </div>
              <p className="text-xs text-foreground-400 mt-1.5">Médias réutilisés depuis le socle central.</p>
            </div>
          )}

          {/* Vidéo courte */}
          <div>
            <label className="block text-sm font-semibold text-foreground-800 mb-2">{t('prf.short_video', { defaultValue: 'Vidéo courte' })}</label>
            <input ref={videoInputRef} type="file" accept={MEDIA_LIMITS.video.accept} onChange={handleVideoChange} className="hidden" />
            <div onClick={() => videoInputRef.current?.click()} className={dropZoneClasses(!!videoFile)}>
              {videoFile ? (
                <div className="relative w-full h-full rounded-xl overflow-hidden bg-black">
                  <video src={videoFile.preview} controls className="w-full h-full object-contain max-h-[300px]" />
                  <button type="button" onClick={(ev) => { ev.stopPropagation(); removeVideo(); }}
                    className="absolute top-2 right-2 w-7 h-7 flex items-center justify-center rounded-full bg-black/50 text-white hover:bg-red-500 transition-colors cursor-pointer z-10">
                    <i className="ri-close-line text-sm"></i>
                  </button>
                  <div className="absolute bottom-2 left-2 px-2 py-0.5 bg-black/50 text-white rounded text-xs z-10">
                    {formatFileSize(videoFile.file.size)}
                  </div>
                </div>
              ) : (
                <>
                  <div className="w-10 h-10 flex items-center justify-center rounded-full bg-background-100 text-foreground-400">
                    <i className="ri-video-line text-xl"></i>
                  </div>
                  <div className="text-center">
                    <p className="text-sm text-foreground-600 font-medium">{t('prf.click_drag_video', { defaultValue: 'Cliquez ou glissez une vidéo' })}</p>
                    <p className="text-xs text-foreground-400 mt-0.5">Max {MEDIA_LIMITS.video.label} — MP4, WebM</p>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>

        {/* Assistant IA + SEO */}
        <div className="bg-background-50 border border-background-200/70 rounded-xl p-6 space-y-5">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-full bg-primary-50 text-primary-600 flex items-center justify-center flex-shrink-0">
              <i className="ri-sparkling-2-line text-lg"></i>
            </div>
            <div className="flex-1">
              <h3 className="text-base font-bold font-heading text-foreground-900">Remplissage par IA</h3>
              <p className="text-xs text-foreground-500 mt-0.5">
                Téléversez d’abord les images du produit ci-dessus, puis cliquez : l’IA analyse les images et remplit automatiquement le nom, la description, la catégorie, le prix conseillé, le stock, le coût d’achat et tous les champs SEO pour un meilleur référencement sur Google.
              </p>
              <button
                type="button"
                onClick={handleAiFill}
                disabled={aiLoading}
                className="mt-3 inline-flex items-center gap-2 px-4 py-2.5 bg-primary-500 text-background-50 rounded-full text-sm font-medium whitespace-nowrap hover:bg-primary-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors cursor-pointer"
              >
                {aiLoading ? (<><i className="ri-loader-4-line animate-spin"></i>Analyse en cours...</>) : (<><i className="ri-magic-line"></i>Remplir par IA</>)}
              </button>
              {aiMsg && (
                <div className={`mt-3 px-3 py-2 rounded-lg text-xs ${aiMsg.type === 'success' ? 'bg-accent-50 text-accent-700' : 'bg-red-50 text-red-600'}`}>
                  <div className="flex flex-wrap items-center gap-2">
                    <span>{aiMsg.text}</span>
                    {aiMsg.action === 'pickImage' && (
                      <button
                        type="button"
                        onClick={() => coverInputRef.current?.click()}
                        className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-primary-500 text-background-50 font-semibold whitespace-nowrap hover:bg-primary-600 transition-colors cursor-pointer"
                      >
                        <i className="ri-image-add-line"></i> Choisir une image
                      </button>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="pt-4 border-t border-background-200/70 space-y-4">
            <h4 className="text-sm font-semibold text-foreground-800 flex items-center gap-2">
              <i className="ri-google-line text-secondary-600"></i>
              Référencement Google (SEO)
            </h4>
            <div>
              <label className="block text-sm font-medium text-foreground-700 mb-1.5">Titre SEO <span className="text-foreground-400 font-normal">(60 car. max)</span></label>
              <input
                type="text"
                value={seoTitle}
                onChange={(e) => setSeoTitle(e.target.value)}
                maxLength={70}
                placeholder="Titre affiché dans les résultats Google"
                className="w-full px-3 py-2.5 bg-background-50 border border-background-200/70 rounded-lg text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300 transition-colors"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-foreground-700 mb-1.5">Meta description <span className="text-foreground-400 font-normal">(160 car. max)</span></label>
              <textarea
                value={seoDescription}
                onChange={(e) => setSeoDescription(e.target.value)}
                maxLength={180}
                rows={2}
                placeholder="Résumé incitatif affiché sous le titre dans Google"
                className="w-full px-3 py-2.5 bg-background-50 border border-background-200/70 rounded-lg text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300 transition-colors resize-none"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-foreground-700 mb-1.5">Mots-clés <span className="text-foreground-400 font-normal">(séparés par des virgules)</span></label>
              <input
                type="text"
                value={seoKeywords}
                onChange={(e) => setSeoKeywords(e.target.value)}
                placeholder="Ex : t-shirt coton, vêtement homme, mode"
                className="w-full px-3 py-2.5 bg-background-50 border border-background-200/70 rounded-lg text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300 transition-colors"
              />
            </div>
          </div>
        </div>

        {/* Fichier digital */}
        {productType === 'digital' && (
        <div className="bg-background-50 border border-background-200/70 rounded-xl p-6 space-y-5">
          <h3 className="text-base font-bold font-heading text-foreground-900 pb-3 border-b border-background-200/70">Fichier digital</h3>
          <input ref={digitalInputRef} type="file" onChange={handleDigitalChange} className="hidden" />
          <div onClick={() => digitalInputRef.current?.click()} className={dropZoneClasses(!!digitalFile)}>
            {digitalFile ? (
              <div className="flex items-center gap-3 w-full">
                <div className="w-11 h-11 rounded-lg bg-background-100 flex items-center justify-center flex-shrink-0">
                  <i className="ri-file-line text-xl text-primary-500"></i>
                </div>
                <div className="min-w-0 flex-1 text-left">
                  <p className="text-sm font-medium text-foreground-800 truncate">{digitalFile.file.name}</p>
                  <p className="text-xs text-foreground-400">{formatFileSize(digitalFile.file.size)}</p>
                </div>
                <button type="button" onClick={(ev) => { ev.stopPropagation(); removeDigital(); }}
                  className="w-7 h-7 flex items-center justify-center rounded-full bg-background-100 text-foreground-400 hover:text-red-500 transition-colors cursor-pointer flex-shrink-0">
                  <i className="ri-close-line text-sm"></i>
                </button>
              </div>
            ) : (
              <>
                <div className="w-10 h-10 flex items-center justify-center rounded-full bg-background-100 text-foreground-400">
                  <i className="ri-upload-cloud-2-line text-xl"></i>
                </div>
                <div className="text-center">
                  <p className="text-sm text-foreground-600 font-medium">Téléversez le fichier à livrer</p>
                  <p className="text-xs text-foreground-400 mt-0.5">PDF, vidéo, ZIP… Max {DIGITAL_FILE_LIMIT_LABEL}</p>
                </div>
              </>
            )}
          </div>
          {errors.digitalFile && <p className="text-xs text-red-500">{errors.digitalFile}</p>}
          <div>
            <label className="block text-sm font-semibold text-foreground-800 mb-1.5">Note de livraison (optionnel)</label>
            <textarea value={digitalNote} onChange={(e) => setDigitalNote(e.target.value)} rows={2} maxLength={500}
              placeholder="Ex : Le lien de téléchargement apparaîtra après votre achat."
              className="w-full px-3 py-2.5 bg-background-50 border border-background-200/70 rounded-lg text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300 transition-colors resize-none" />
          </div>
          <p className="text-xs text-foreground-400">Les produits digitaux n&apos;ont ni stock ni adresse de livraison : le fichier est mis à disposition de l&apos;acheteur après sa commande.</p>
        </div>
        )}

        {/* Variantes */}
        <div className="bg-background-50 border border-background-200/70 rounded-xl p-6 space-y-5">
          <div className="flex items-center justify-between pb-3 border-b border-background-200/70">
            <h3 className="text-base font-bold font-heading text-foreground-900">{t('prf.variants', { defaultValue: 'Variantes' })}</h3>
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={hasVariants} onChange={(e) => { setHasVariants(e.target.checked); if (!e.target.checked) { setVariantDefs([]); setSkus([]); } }}
                className="w-4 h-4 rounded border-background-300/60 text-primary-500 focus:ring-primary-400 cursor-pointer" />
              <span className="text-sm text-foreground-600">{t('prf.has_variants', { defaultValue: 'Ce produit a des variantes (tailles, couleurs...)' })}</span>
            </label>
          </div>

          {hasVariants && (
            <>
              <div className="space-y-3">
                {variantDefs.map((def) => (
                  <div key={def.id} className="flex items-start gap-3 p-3 bg-background-100/70 rounded-lg">
                    <div className="flex-1 grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <input type="text" value={def.name} onChange={(e) => updateVariantDef(def.id, 'name', e.target.value)}
                        placeholder={t('prf.variant_type_ph', { defaultValue: 'Type (ex: Taille)' })} className="w-full px-3 py-2 bg-background-50 border border-background-200/70 rounded-lg text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300" />
                      <input type="text" value={def.optionsText} onChange={(e) => updateVariantDef(def.id, 'optionsText', e.target.value)}
                        placeholder={t('prf.variant_options_ph', { defaultValue: 'Options (ex: S, M, L)' })} className="w-full px-3 py-2 bg-background-50 border border-background-200/70 rounded-lg text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300" />
                    </div>
                    <button type="button" onClick={() => removeVariantDef(def.id)}
                      className="w-8 h-8 flex items-center justify-center rounded-md text-foreground-400 hover:text-red-500 hover:bg-red-50 transition-colors cursor-pointer flex-shrink-0 mt-0.5">
                      <i className="ri-close-line"></i>
                    </button>
                  </div>
                ))}
                <button type="button" onClick={addVariantDef}
                  className="flex items-center gap-2 px-4 py-2 bg-secondary-50 text-secondary-700 rounded-full text-sm font-medium whitespace-nowrap hover:bg-secondary-100 transition-colors cursor-pointer">
                  <i className="ri-add-line"></i> {t('prf.add_variant_type', { defaultValue: 'Ajouter un type de variante' })}
                </button>
              </div>

              {skus.length > 0 && (
                <div className="space-y-3">
                  <div className="flex items-center gap-3">
                    <span className="text-sm font-semibold text-foreground-800">{t('prf.generated_combos', { defaultValue: 'Combinaisons générées' })} ({skus.length})</span>
                    <div className="flex items-center gap-2 ml-auto">
                      <span className="text-xs text-foreground-500">{t('prf.pricing', { defaultValue: 'Tarification :' })}</span>
                      <button type="button" onClick={() => setPricingMode('same')}
                        className={`px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap cursor-pointer transition-colors ${pricingMode === 'same' ? 'bg-primary-50 text-primary-700' : 'bg-background-100 text-foreground-500 hover:bg-background-200/70'}`}>
                        {t('prf.single_price', { defaultValue: 'Prix unique' })}
                      </button>
                      <button type="button" onClick={() => setPricingMode('per_variant')}
                        className={`px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap cursor-pointer transition-colors ${pricingMode === 'per_variant' ? 'bg-primary-50 text-primary-700' : 'bg-background-100 text-foreground-500 hover:bg-background-200/70'}`}>
                        {t('prf.per_variant', { defaultValue: 'Prix par variante' })}
                      </button>
                    </div>
                  </div>

                  <div className="overflow-x-auto rounded-lg border border-background-200/70">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="bg-background-100 text-xs font-semibold text-foreground-500 uppercase tracking-wider">
                          <th className="text-left px-4 py-2.5">{t('prf.combination', { defaultValue: 'Combinaison' })}</th>
                          {pricingMode === 'per_variant' && <th className="text-left px-4 py-2.5">{t('prf.price', { defaultValue: 'Prix' })} ({currency})</th>}
                          <th className="text-left px-4 py-2.5">{t('prf.stock', { defaultValue: 'Stock' })}</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-background-200/70">
                        {skus.map((sku, idx) => (
                          <tr key={idx} className="hover:bg-background-50/50 transition-colors">
                            <td className="px-4 py-2.5">
                              <span className="text-foreground-800 font-medium">{sku.label}</span>
                            </td>
                            {pricingMode === 'per_variant' && (
                              <td className="px-4 py-2.5">
                                <input type="number" step="0.01" min="0" value={sku.price}
                                  onChange={(e) => updateSkuField(idx, 'price', e.target.value)}
                                  className={`w-24 px-2 py-1.5 bg-background-50 border rounded text-sm text-foreground-900 focus:outline-none focus:border-primary-300 ${errors[`sku_price_${idx}`] ? 'border-red-300' : 'border-background-200/70'}`} />
                                {errors[`sku_price_${idx}`] && <p className="text-xs text-red-500 mt-0.5">{errors[`sku_price_${idx}`]}</p>}
                              </td>
                            )}
                            <td className="px-4 py-2.5">
                              <input type="number" min="0" value={sku.stock}
                                onChange={(e) => updateSkuField(idx, 'stock', e.target.value)}
                                className="w-20 px-2 py-1.5 bg-background-50 border border-background-200/70 rounded text-sm text-foreground-900 focus:outline-none focus:border-primary-300" />
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        {/* Options avancées — repliées par défaut pour garder la page simple */}
        <div className="bg-background-50 border border-background-200/70 rounded-xl overflow-hidden">
          <button
            type="button"
            onClick={() => setAdvancedOpen((v) => !v)}
            className="w-full flex items-center gap-3 p-5 text-left hover:bg-background-100/50 transition-colors cursor-pointer"
          >
            <div className="w-9 h-9 rounded-lg bg-secondary-100 text-secondary-700 flex items-center justify-center flex-shrink-0">
              <i className="ri-equalizer-line"></i>
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="text-base font-bold font-heading text-foreground-900">Options avancées</h3>
              <p className="text-xs text-foreground-500 mt-0.5">Entité centrale : référence (SKU), code-barres, sous-catégorie, visibilité, TVA, tags et traductions. Tout est facultatif.</p>
            </div>
            <i className={`ri-arrow-down-s-line text-xl text-foreground-400 transition-transform ${advancedOpen ? 'rotate-180' : ''}`}></i>
          </button>

          {advancedOpen && (
            <div className="px-5 pb-5 pt-5 space-y-5 border-t border-background-200/70">
              <div className="flex items-start gap-3">
                <div className="w-9 h-9 rounded-lg bg-secondary-100 text-secondary-700 flex items-center justify-center flex-shrink-0">
                  <i className="ri-database-2-line"></i>
                </div>
                <div>
                  <h3 className="text-base font-bold font-heading text-foreground-900">Entité centrale</h3>
                  <p className="text-xs text-foreground-500 mt-0.5">Champs partagés par tous les modules Zifek (Commerce, Caisse, Stock, Commandes, Marketing, Finance). Tous ces champs sont facultatifs.</p>
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-foreground-800 mb-1.5">Référence (SKU) <span className="text-foreground-400 font-normal">(optionnel)</span></label>
                  <input type="text" value={sku} onChange={(e) => setSku(e.target.value)} placeholder="Ex : TSH-001"
                    className="w-full px-3 py-2.5 bg-background-50 border border-background-200/70 rounded-lg text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300 transition-colors" />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-foreground-800 mb-1.5">Code-barres <span className="text-foreground-400 font-normal">(optionnel)</span></label>
                  <input type="text" value={barcode} onChange={(e) => setBarcode(e.target.value)} placeholder="EAN / UPC"
                    className="w-full px-3 py-2.5 bg-background-50 border border-background-200/70 rounded-lg text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300 transition-colors" />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-foreground-800 mb-1.5">Sous-catégorie <span className="text-foreground-400 font-normal">(optionnel)</span></label>
                  <input type="text" value={subcategory} onChange={(e) => setSubcategory(e.target.value)} placeholder="Ex : T-shirts"
                    className="w-full px-3 py-2.5 bg-background-50 border border-background-200/70 rounded-lg text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300 transition-colors" />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-foreground-800 mb-1.5">Visibilité</label>
                  <select value={visibility} onChange={(e) => setVisibility(e.target.value)}
                    className="w-full px-3 py-2.5 bg-background-50 border border-background-200/70 rounded-lg text-sm text-foreground-900 focus:outline-none focus:border-primary-300 cursor-pointer">
                    {PRODUCT_VISIBILITIES.map((v) => (<option key={v.key} value={v.key}>{v.label}</option>))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-semibold text-foreground-800 mb-1.5">Taux de TVA (%)</label>
                  <input type="number" step="0.1" min="0" value={taxRate} onChange={(e) => setTaxRate(e.target.value)} placeholder="0"
                    className="w-full px-3 py-2.5 bg-background-50 border border-background-200/70 rounded-lg text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300 transition-colors" />
                </div>
                <div className="flex items-end">
                  <label className="flex items-center gap-3 cursor-pointer pb-2.5">
                    <input type="checkbox" checked={taxIncluded} onChange={(e) => setTaxIncluded(e.target.checked)}
                      className="w-4 h-4 rounded border-background-300/60 text-primary-500 focus:ring-primary-400 cursor-pointer" />
                    <span className="text-sm text-foreground-700 whitespace-nowrap">Prix TTC (taxe incluse)</span>
                  </label>
                </div>
              </div>

              {productType === 'physical' && (
                <label className="flex items-center gap-3 cursor-pointer">
                  <input type="checkbox" checked={stockTracking} onChange={(e) => setStockTracking(e.target.checked)}
                    className="w-4 h-4 rounded border-background-300/60 text-primary-500 focus:ring-primary-400 cursor-pointer" />
                  <span className="text-sm text-foreground-700">Suivi du stock activé (les fonctions avancées restent dans Zifek Inventory)</span>
                </label>
              )}

              <TagSelector
                organizationId={orgId}
                value={tagIds}
                onChange={setTagIds}
                scope="product"
                label="Tags"
              />

              <TranslationEditor
                languages={translationLangs}
                value={translations}
                onChange={setTranslations}
                label="Traductions du produit"
                hint="Renseignez l'intitulé et la description du produit dans chaque langue (une seule fiche, plusieurs versions)."
              />
            </div>
          )}
        </div>

        {/* Submit */}
        <div className="flex items-center gap-3">
          <button type="submit" disabled={saving}
            className="flex items-center gap-2 px-6 py-2.5 bg-primary-500 text-background-50 rounded-full text-sm font-medium whitespace-nowrap hover:bg-primary-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors cursor-pointer">
            {saving ? (<><i className="ri-loader-4-line animate-spin"></i>{uploading ? t('prf.uploading', { defaultValue: 'Téléchargement...' }) : t('prf.creating', { defaultValue: 'Création...' })}</>) : (<><i className="ri-check-line"></i>{t('prf.create_product', { defaultValue: 'Créer le produit' })}</>)}
          </button>
          <button type="button" onClick={() => navigate('/dashboard/products')}
            className="px-4 py-2.5 bg-background-100 text-foreground-600 rounded-full text-sm font-medium whitespace-nowrap hover:bg-background-200/70 transition-colors cursor-pointer">
            {t('prf.cancel', { defaultValue: 'Annuler' })}
          </button>
        </div>
      </form>

      <MediaLibraryPicker
        isOpen={libraryOpen}
        onClose={() => setLibraryOpen(false)}
        ownerId={getCommerceId(user)}
        onSelect={(urls) => setLibraryUrls((prev) => Array.from(new Set([...prev, ...urls])))}
        initialSelected={libraryUrls}
      />
    </div>
  );
}